import { beginNavigation, endNavigation, redirectTo } from '../../shared/navigation';
import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';
import { authApi, platformApi } from '../../core/api/client';
import type { ProductCode } from '../../core/api/types';
import { ROUTES } from '../../app/routes';
import { productDestination } from './productDestination';

const CLIENT_PRODUCTS = new Set<ProductCode>(['RESTAURANT', 'HOTEL', 'PHARMACY', 'AUTOSERVICE', 'COSMETICS']);
const launchFlights = new Map<ProductCode, Promise<void>>();

function launchProduct(productCode: ProductCode): Promise<void> {
  const existing = launchFlights.get(productCode);
  if (existing) return existing;
  const flight = (async () => {
    const products = await platformApi.launcher();
    const product = products.find((item) => item.code === productCode);
    if (!product) throw new Error(`You do not have access to ${productCode}.`);
    const destination = productDestination(productCode, product.launchUrl, window.location.origin);
    const { code } = await authApi.createProductLaunch(productCode);
    destination.searchParams.set('launch_code', code);
    redirectTo(destination.toString(), true);
  })();
  launchFlights.set(productCode, flight);
  void flight.catch(() => launchFlights.delete(productCode));
  return flight;
}

export function LaunchProductPage() {
  const { productCode: rawCode } = useParams();
  const productCode = rawCode?.toUpperCase() as ProductCode | undefined;
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!productCode || !CLIENT_PRODUCTS.has(productCode)) return;
    beginNavigation();
    let active = true;
    void launchProduct(productCode).catch((caught) => {
      if (active) endNavigation();
      if (active) setError(caught instanceof Error ? caught.message : 'The product could not be opened.');
    });
    return () => { active = false; };
  }, [productCode, attempt]);

  if (!productCode || !CLIENT_PRODUCTS.has(productCode)) return <Navigate to={ROUTES.home} replace />;
  return (
    <Box sx={{ minHeight: '55vh', display: 'grid', placeItems: 'center' }}>
      {error ? <Alert severity="error" action={<Button color="inherit" onClick={() => {
        setError(null);
        setAttempt((value) => value + 1);
      }}>Retry</Button>}>{error}</Alert> : (
        <Box sx={{ textAlign: 'center' }}><CircularProgress /><Typography sx={{ mt: 2 }}>Opening {productCode.toLowerCase()}…</Typography></Box>
      )}
    </Box>
  );
}
